/**
 * Favorites entry helpers (Phase 2a — data-shape tolerance).
 *
 * A favorites entry stored in `privateData.favorites` is EITHER:
 *   - a legacy bare id string ("uuid-string"), or
 *   - an object { id, savedAt, ... } (Phase 2 shape; field enrichment in 2b).
 *
 * Everything that reads or writes favorites routes through these helpers so the
 * two shapes coexist transparently. No visible behavior changes in 2a — hearts
 * read/toggle exactly as before; entries just persist as objects going forward.
 */

// Normalize a single entry to its listing-id string.
export const favoriteId = entry => (typeof entry === 'string' ? entry : entry?.id);

// Membership test tolerant of either shape.
export const hasFavorite = (favs, id) => favs.some(f => favoriteId(f) === id);

// Add (idempotent). New entries persist as { id, savedAt }. Existing entries
// (string or object) are left untouched.
export const addFavorite = (favs, id) =>
  hasFavorite(favs, id) ? favs : [...favs, { id, savedAt: Date.now() }];

// Remove by id, tolerant of either shape.
export const removeFavorite = (favs, id) => favs.filter(f => favoriteId(f) !== id);

// Project the entry array down to a plain id-string list.
export const favoriteIdList = favs => favs.map(favoriteId);

/* ─── Phase 2b: snapshots, change-signals, sort ──────────────────
 *
 * Each entry may also carry `lastSeen` — a snapshot of the listing as of the
 * buyer's previous favorites-page visit. We diff the CURRENT listing against
 * that snapshot to surface "since you last visited" change-signals. savedAt and
 * lastSeen are the only enrichment fields; absent on legacy/first-visit entries.
 */

// Snapshot accessors (tolerant of legacy bare-string entries → null).
export const lastSeenOf = entry => (typeof entry === 'string' ? null : entry?.lastSeen || null);
export const savedAtOf = entry =>
  typeof entry === 'string' ? null : entry?.savedAt ?? null;

/**
 * Story 11.16 AC4 — DOES THIS LISTING HAVE A VIDEO ON THE BUYER PAGE?
 *
 * It used to be `!!pd.videoUrl`, which read ONE of the two fields the gallery
 * renders. That was already wrong for every listing whose only video sits in
 * `additionalVideos` — the DOMINANT shape since the seller wizard retired
 * `videoUrl` — and Story 11.16 makes it actively destructive: the relocation
 * script MOVES 148 listings' videoUrl into `additionalVideos`, and the
 * produced-video writeback stops writing `videoUrl` at all. Read `videoUrl`
 * alone and every one of those listings flips hasVideo TRUE -> FALSE while the
 * buyer page is unchanged, and `new_video` can never fire again for anything,
 * ever, because nothing writes the field the predicate reads.
 *
 * Considering BOTH fields makes the relocation INVISIBLE here, which is the
 * point: the value moves between two fields this predicate treats identically,
 * so hasVideo is invariant across the move.
 *
 * The `videoUrl` half is byte-identical to what shipped (`!!`, not a trim), so
 * no existing listing changes verdict on that field alone. The array half is
 * trim-aware because `additionalVideos` is a seller-entered string[] and a
 * whitespace row is not a video.
 *
 * NOT id-aware, on purpose. `SectionGallery` filters by extracted YouTube id,
 * so an id-aware test would match what renders more closely — but it would also
 * flip hasVideo to false for listings carrying a non-YouTube `videoUrl`, which
 * is a behaviour change this story does not own. Truthiness across both fields
 * is what makes the relocation a no-op; matching the gallery exactly is
 * separate work.
 */
export const listingHasVideo = pd =>
  !!pd?.videoUrl ||
  (Array.isArray(pd?.additionalVideos) &&
    pd.additionalVideos.some(v => typeof v === 'string' && v.trim()));

// Normalize a listing's raw publicData.listingStatus to the snapshot vocabulary.
// Anything that isn't sold/on_hold (incl. undefined) is treated as active.
export const listingStatusOf = listing => {
  const s = listing?.attributes?.publicData?.listingStatus;
  return s === 'sold' ? 'sold' : s === 'on_hold' ? 'on_hold' : 'active';
};

// Build a lastSeen snapshot from a denormalized listing (image best-effort).
export const snapshotFromListing = listing => {
  const a = listing?.attributes || {};
  const pd = a.publicData || {};
  const variants = listing?.images?.[0]?.attributes?.variants || {};
  const image = variants['listing-card']?.url || variants['listing-card-2x']?.url || null;
  return {
    title: a.title ?? null,
    image,
    price: typeof a.price?.amount === 'number' ? a.price.amount : null,
    priceCurrency: a.price?.currency ?? null,
    status: listingStatusOf(listing),
    hasVideo: listingHasVideo(pd),
    year: pd.birthYear ?? null,
    color: pd.color ?? null,
    sex: pd.sex ?? null,
    discipline: pd.disciplines?.[0] ?? null,
  };
};

// Signal keys (also the chip/filter order used by the UI).
export const SIGNALS = ['price_drop', 'on_hold', 'new_video', 'back_available', 'sold'];

// Diff the current listing against the AS-LOADED lastSeen snapshot.
// Returns an array of signal keys (empty for first-visit/legacy null → no signals,
// or when nothing changed).
export const computeSignals = (lastSeen, listing) => {
  if (!lastSeen || !listing) return [];
  const a = listing.attributes || {};
  const pd = a.publicData || {};
  const status = listingStatusOf(listing);
  const out = [];

  const curAmt = a.price?.amount;
  const curCur = a.price?.currency;
  if (
    typeof curAmt === 'number' &&
    typeof lastSeen.price === 'number' &&
    curCur === lastSeen.priceCurrency &&
    curAmt < lastSeen.price
  ) {
    out.push('price_drop');
  }
  if (status === 'on_hold' && lastSeen.status !== 'on_hold') out.push('on_hold');
  if (status === 'sold' && lastSeen.status !== 'sold') out.push('sold');
  if (status === 'active' && (lastSeen.status === 'on_hold' || lastSeen.status === 'sold')) {
    out.push('back_available');
  }
  // 11.16 AC4 — THE SAME PREDICATE as the snapshot writer above. These two must
  // never diverge: `snapshotFromListing` writes `hasVideo` and this reads it
  // back, so a predicate that differs by one field manufactures a signal that
  // fires on every visit forever (or, as before the fix, a permanently dead
  // one).
  if (listingHasVideo(pd) && lastSeen.hasVideo === false) out.push('new_video');
  return out;
};

// Comparator factory for the Available section sort pill.
//   'recent'     → savedAt desc, null/legacy entries last (default)
//   'price_asc'  → current price low→high, no-price last
//   'price_desc' → current price high→low, no-price last
// `priceOf` extracts the current numeric price (amount) for a view model.
export const favoriteComparator = (sortKey, priceOf, savedAtById) => (a, b) => {
  if (sortKey === 'price_asc' || sortKey === 'price_desc') {
    const pa = priceOf(a);
    const pb = priceOf(b);
    const aHas = typeof pa === 'number';
    const bHas = typeof pb === 'number';
    if (aHas && bHas) return sortKey === 'price_asc' ? pa - pb : pb - pa;
    if (aHas) return -1; // priced before unpriced
    if (bHas) return 1;
    return 0;
  }
  // 'recent' (default): savedAt desc, null/legacy last.
  const sa = savedAtById(a);
  const sb = savedAtById(b);
  const aHas = typeof sa === 'number';
  const bHas = typeof sb === 'number';
  if (aHas && bHas) return sb - sa;
  if (aHas) return -1;
  if (bHas) return 1;
  return 0;
};

/**
 * SAFE read-modify-write merge for the "mark seen on load" write-back.
 *
 * @param {Array}  freshFavorites  the FRESHEST favorites array (string|object)
 * @param {Map}    listingsById    Map of id → denormalized listing that came back
 * @returns {Array} the favorites array to persist
 *
 * Rules: iterate the FRESH favorites only (never resurrect a removed id);
 * lazily upgrade bare strings to objects; preserve savedAt; for ids whose
 * listing returned, refresh lastSeen from the listing; for ids whose listing did
 * NOT return (closed/deleted), preserve the existing lastSeen (never null it).
 */
export const mergeSeenSnapshots = (freshFavorites, listingsById) =>
  (freshFavorites || []).map(entry => {
    const id = favoriteId(entry);
    const savedAt = savedAtOf(entry);
    const listing = listingsById.get(id);
    const lastSeen = listing ? snapshotFromListing(listing) : lastSeenOf(entry);
    const next = { id };
    if (savedAt != null) next.savedAt = savedAt;
    if (lastSeen) next.lastSeen = lastSeen;
    return next;
  });
